/**
 * The particle kinds and the texture tables the spawners draw from. A kind
 * selects the per-frame step in ParticleContainer; the tables hold the
 * numbered sprite names, index 0 first, checked against the loaded textures.
 */
import type { LoadedTextureName } from '../types/Textures';

export const particleTypeIdByName = {
  MISSILE: 0,
  MISSILE_FAT: 1,
  MISSILE_SMALL: 2,
  SHOCKWAVE_SMOKE: 3,
  SHOCKWAVE_INNER: 4,
  SHOCKWAVE_OUTER: 5,
  EXPLOSION_FLASH: 6,
  EXPLOSION_FLASH_BIG: 7,
  EXPLOSION_SMOKE: 8,
  EXPLOSION_HOT_SMOKE: 9,
  EXPLOSION_SPARK: 10,
  FRAGMENT_SMOKE: 11,
  PLANE_DAMAGE: 12,
  /** This client's addition: a wide additive glow over an explosion, from the missile-light work. */
  EXPLOSION_LIGHT: 13,
} as const;

/** A particle kind: one of the ids in particleTypeIdByName. */
export type ParticleTypeId = (typeof particleTypeIdByName)[keyof typeof particleTypeIdByName];

const FOUR = [1, 2, 3, 4] as const;
const SIXTEEN = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const;

/** The numbered sprite names of one texture family. */
function numbered<P extends string, N extends number>(prefix: P, numbers: readonly N[]): readonly `${P}_${N}`[] {
  return numbers.map((n) => `${prefix}_${n}` as const);
}

export const sparkTextures = numbered('spark', FOUR) satisfies readonly LoadedTextureName[];
export const flashTextures = numbered('flash', FOUR) satisfies readonly LoadedTextureName[];
export const hotsmokeTextures = numbered('hotsmoke', FOUR) satisfies readonly LoadedTextureName[];

/** A smoke texture and its shadow, drawn together for every puff that casts one. */
export type SmokePair = readonly [smoke: LoadedTextureName, shadow: LoadedTextureName];

/** The sixteen smoke textures, each paired with its shadow. */
export const smokePairs = SIXTEEN.map(
  (n) => [`smoke_${n}`, `smokeshadow_${n}`] as const,
) satisfies readonly SmokePair[];

/** The sixteen smoke textures alone, for the puffs without a shadow. */
export const smokeTextures: readonly LoadedTextureName[] = smokePairs.map(([smoke]) => smoke);
